import { useUserProfile } from '../../contexts/UserProfileContext';
import { useOnboardingWizardControl } from '../OnboardingWizard/OnboardingWizardProvider';
import { USER_TYPES, type UserType } from '../../types/UserProfile';

export interface DefaultAppRouteAccess {
  canAccessDashboard: boolean;
  canAccessAccounts: boolean;
  canAccessUsers: boolean;
  canAccessContentManagement: boolean;
  canAccessCourses: boolean;
  canAccessAiRecommendation: boolean;
}

/** Role-based home route — same priority as post-login `DefaultRedirect`. */
export function resolveDefaultAppPath(userType: UserType, access: DefaultAppRouteAccess): string {
  switch (userType) {
    case USER_TYPES.SUPER_ADMIN:
    case USER_TYPES.ACCOUNT_ADMIN:
      if (access.canAccessDashboard) return '/review-center';
      if (access.canAccessAccounts) return '/accounts';
      if (access.canAccessUsers) return '/operators';
      if (access.canAccessContentManagement) return '/manage-content';
      if (access.canAccessCourses) return '/courses';
      break;

    case USER_TYPES.CONTENT_MANAGER:
      if (access.canAccessContentManagement) return '/manage-content';
      if (access.canAccessCourses) return '/courses';
      break;

    case USER_TYPES.STANDARD:
      if (access.canAccessAiRecommendation) return '/ai-recommendation';
      if (access.canAccessCourses) return '/courses';
      break;

    default:
      break;
  }

  return '/unauthorized';
}

/** Resolves the same default route used after login (`DefaultRedirect`). */
export function useDefaultAppRoute(): string {
  const {
    userType,
    canAccessDashboard,
    canAccessAccounts,
    canAccessUsers,
    canAccessContentManagement,
    canAccessCourses,
    canAccessAiRecommendation,
  } = useUserProfile();

  const { requiresRootOnboardingRoute } = useOnboardingWizardControl();

  if (requiresRootOnboardingRoute) {
    return '/';
  }

  return resolveDefaultAppPath(userType, {
    canAccessDashboard,
    canAccessAccounts,
    canAccessUsers,
    canAccessContentManagement,
    canAccessCourses,
    canAccessAiRecommendation,
  });
}
